import { useState } from "react";
import { useToast } from "@/components/base/Toast";

type PlatformTab = "facebook" | "instagram";
type FbStep = 1 | 2 | 3;

interface PageCard {
  id: string;
  name: string;
  followers: string;
}

interface AdAccount {
  id: string;
  name: string;
  accountId: string;
  campaignCount: number;
}

interface FieldMapping {
  fbField: string;
  crmField: string;
}

const mockPages: PageCard[] = [
  { id: "page-1", name: "PropMax Solutions", followers: "12.4K" },
  { id: "page-2", name: "PropMax Realty Deals", followers: "3.2K" },
];

const mockAdAccounts: AdAccount[] = [
  { id: "act-1", name: "PropMax Solutions", accountId: "Act_123456789", campaignCount: 6 },
  { id: "act-2", name: "PropMax Campaigns", accountId: "Act_987654321", campaignCount: 3 },
];

const defaultFbMappings: FieldMapping[] = [
  { fbField: "full_name", crmField: "Lead Name" },
  { fbField: "phone_number", crmField: "Phone" },
  { fbField: "email", crmField: "Email" },
  { fbField: "city", crmField: "City" },
  { fbField: "budget_range", crmField: "Notes" },
  { fbField: "what_are_you_looking_for", crmField: "Source Notes" },
];

const crmFields = [
  "Lead Name",
  "Phone",
  "Email",
  "City",
  "Property Type",
  "Budget",
  "Notes",
  "Source",
  "Source Notes",
];

export function SocialMediaSection() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<PlatformTab>("facebook");

  // Facebook state
  const [fbStep, setFbStep] = useState<FbStep>(1);
  const [fbAppId, setFbAppId] = useState("");
  const [fbAppSecret, setFbAppSecret] = useState("");
  const [fbPageToken, setFbPageToken] = useState("");
  const [selectedFbPage, setSelectedFbPage] = useState<string | null>(null);
  const [selectedFbAccounts, setSelectedFbAccounts] = useState<string[]>([]);
  const [fbMappings, setFbMappings] = useState<FieldMapping[]>(defaultFbMappings);
  const [fbConnected, setFbConnected] = useState(false);

  const toggleFbAccount = (id: string) => {
    setSelectedFbAccounts((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const handleFbConnectAndContinue = () => {
    if (!fbAppId || !fbAppSecret || !fbPageToken) {
      showToast("Please fill in all Meta App credentials", "error");
      return;
    }
    if (!selectedFbPage) {
      showToast("Please select a Facebook page", "error");
      return;
    }
    setFbStep(2);
  };

  const handleFbContinue = () => {
    if (selectedFbAccounts.length === 0) {
      showToast("Please select at least one Ad Account", "error");
      return;
    }
    setFbStep(3);
  };

  const handleFbSaveAndActivate = () => {
    setFbConnected(true);
    showToast("Facebook leads integration activated successfully", "success");
  };

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] p-5">
      {/* Header + Tabs */}
      <div>
        <h3 className="text-lg font-semibold text-[#111827]">Social Media Lead Integration</h3>
        <p className="text-sm text-[#6b7280] mt-0.5 mb-5">
          Import leads from Facebook and Instagram lead generation campaigns
        </p>

        {/* Platform Tabs */}
        <div className="flex border-b border-gray-200 mb-5">
          <button
            onClick={() => setActiveTab("facebook")}
            className={`pb-2.5 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === "facebook"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-[#6b7280] hover:text-[#374151]"
            }`}
          >
            <i className="ri-facebook-circle-fill mr-1.5" />
            Facebook Leads
          </button>
          <button
            onClick={() => setActiveTab("instagram")}
            className={`pb-2.5 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ml-6 ${
              activeTab === "instagram"
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-[#6b7280] hover:text-[#374151]"
            }`}
          >
            <i className="ri-instagram-line mr-1.5" />
            Instagram Leads
          </button>
        </div>
      </div>

      {/* ──── FACEBOOK TAB ──── */}
      {activeTab === "facebook" && !fbConnected && (
        <>
          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-6">
            {(["Connect Meta App", "Select Ad Account", "Map Fields"] as const).map(
              (label, i) => {
                const stepNum = (i + 1) as FbStep;
                const isActive = fbStep === stepNum;
                const isDone = fbStep > stepNum;
                return (
                  <div key={label} className="flex items-center gap-2 flex-1 last:flex-none">
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                          isDone
                            ? "bg-indigo-600 text-white"
                            : isActive
                              ? "bg-indigo-600 text-white"
                              : "bg-gray-200 text-[#6b7280]"
                        }`}
                      >
                        {isDone ? (
                          <i className="ri-check-line text-[10px]" />
                        ) : (
                          stepNum
                        )}
                      </div>
                      <span
                        className={`text-xs font-medium whitespace-nowrap ${
                          isActive ? "text-[#111827]" : isDone ? "text-[#6b7280]" : "text-[#9ca3af]"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {i < 2 && <div className="flex-1 h-px bg-gray-200 mx-1" />}
                  </div>
                );
              }
            )}
          </div>

          {/* Step 1: Connect Meta App */}
          {fbStep === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <i className="ri-information-line text-blue-600 text-sm mt-0.5" />
                  <p className="text-xs text-blue-700 leading-relaxed">
                    You need a Meta Business Manager account with active Lead Gen campaigns
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">Meta App ID</label>
                  <input
                    type="text"
                    value={fbAppId}
                    onChange={(e) => setFbAppId(e.target.value)}
                    placeholder="XXXXXXXXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#111827] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1">Meta App Secret</label>
                  <input
                    type="password"
                    value={fbAppSecret}
                    onChange={(e) => setFbAppSecret(e.target.value)}
                    placeholder="••••••••••••••••"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#111827] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Page Access Token</label>
                <input
                  type="password"
                  value={fbPageToken}
                  onChange={(e) => setFbPageToken(e.target.value)}
                  placeholder="••••••••••••••••"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#111827] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <a
                href="#"
                className="inline-block text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                onClick={(e) => {
                  e.preventDefault();
                  showToast("Opening Meta Business Manager...", "info");
                }}
              >
                Generate token in Meta Business Manager
              </a>

              {/* Connected Pages */}
              {fbPageToken.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-sm font-semibold text-[#111827] mb-3">Connected Pages</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {mockPages.map((page) => (
                      <button
                        key={page.id}
                        onClick={() => setSelectedFbPage(page.id)}
                        className={`text-left p-3 rounded-lg border transition-all cursor-pointer ${
                          selectedFbPage === page.id
                            ? "border-2 border-indigo-600 bg-indigo-50"
                            : "border border-gray-200 hover:border-indigo-300 bg-white"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                            <i className="ri-facebook-fill text-white text-lg" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-[#111827] truncate">{page.name}</p>
                            <p className="text-xs text-[#6b7280] mt-0.5">
                              <i className="ri-user-line text-[10px] mr-0.5" />
                              {page.followers} followers
                            </p>
                          </div>
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              selectedFbPage === page.id
                                ? "border-indigo-600 bg-indigo-600"
                                : "border-gray-300"
                            }`}
                          >
                            {selectedFbPage === page.id && (
                              <i className="ri-check-line text-white text-[10px]" />
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleFbConnectAndContinue}
                className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Connect &amp; Continue
              </button>
            </div>
          )}

          {/* Step 2: Select Ad Account */}
          {fbStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-[#374151]">
                Select the Ad Accounts that run your lead generation campaigns
              </p>

              <div className="space-y-3">
                {mockAdAccounts.map((account) => (
                  <div
                    key={account.id}
                    onClick={() => toggleFbAccount(account.id)}
                    className={`p-4 rounded-lg border transition-all cursor-pointer ${
                      selectedFbAccounts.includes(account.id)
                        ? "border-2 border-indigo-600 bg-indigo-50"
                        : "border border-gray-200 hover:border-indigo-300 bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            selectedFbAccounts.includes(account.id)
                              ? "border-indigo-600 bg-indigo-600"
                              : "border-gray-300"
                          }`}
                        >
                          {selectedFbAccounts.includes(account.id) && (
                            <i className="ri-check-line text-white text-[10px]" />
                          )}
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <i className="ri-advertisement-line text-amber-600 text-lg" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-[#111827]">{account.name}</p>
                          <p className="text-xs text-[#6b7280]">{account.accountId}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700">
                          {account.campaignCount} active campaigns
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleFbContinue}
                className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 3: Map Fields */}
          {fbStep === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-[#374151]">
                Map Facebook Lead Gen form fields to your CRM fields
              </p>

              <div className="overflow-hidden border border-gray-200 rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151]">
                        Facebook Field
                      </th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151]">
                        CRM Field
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {fbMappings.map((mapping, idx) => (
                      <tr key={mapping.fbField}>
                        <td className="px-4 py-2.5">
                          <span className="text-sm text-[#111827]">{mapping.fbField}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <select
                            value={mapping.crmField}
                            onChange={(e) => {
                              const newMappings = [...fbMappings];
                              newMappings[idx] = { ...mapping, crmField: e.target.value };
                              setFbMappings(newMappings);
                            }}
                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm text-[#111827] bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          >
                            {crmFields.map((field) => (
                              <option key={field} value={field}>
                                {field}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={handleFbSaveAndActivate}
                className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
              >
                Save &amp; Activate
              </button>
            </div>
          )}
        </>
      )}

      {/* Facebook Connected State */}
      {activeTab === "facebook" && fbConnected && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <i className="ri-check-line text-green-600 text-xl" />
              </div>
              <div>
                <p className="text-sm font-semibold text-green-800">Facebook Leads Connected</p>
                <p className="text-xs text-green-600">
                  {selectedFbPage ? mockPages.find((p) => p.id === selectedFbPage)?.name : "Page"} —{" "}
                  {selectedFbAccounts.length} ad account{selectedFbAccounts.length !== 1 ? "s" : ""} selected
                </p>
              </div>
              <button
                onClick={() => {
                  setFbConnected(false);
                  setFbStep(1);
                  setSelectedFbPage(null);
                  setSelectedFbAccounts([]);
                  showToast("Facebook Leads disconnected", "info");
                }}
                className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium whitespace-nowrap"
              >
                Disconnect
              </button>
            </div>
          </div>

          <button
            onClick={() => showToast("Test lead sent via Facebook Leads API", "success")}
            className="px-4 py-2 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-send-plane-line mr-1.5" />
            Send Test Lead
          </button>
        </div>
      )}

      {/* ──── INSTAGRAM TAB ──── */}
      {activeTab === "instagram" && (
        <div className="py-8">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-lg mx-auto">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-amber-600 text-lg" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">Coming Soon</p>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Instagram Lead Gen API integration coming in next update. Facebook integration works for both
                  platforms via Meta Business.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}